import { DetailOverlay, Label, MonoMeta, ItemEditorBar, NoteOnCard, RefineNoteOnCard, StateWord } from "@/design/primitives";
import { ProjectHeading, ProjectNext } from "./ProjectCard";
import { RunnerLive } from "./RunnerLive";

export const ProjectDetail = ({ card, variant = "desktop", onClose, editor }) => {
  const skills = card.detail?.skills?.length ? card.detail.skills.join(" · ") : "the usual build kit";
  const phases = card.detail?.phases?.length ? card.detail.phases : ["The steps aren't written down yet"];
  const { checked, total } = card.detail?.successTest || { checked: 0, total: 0 };
  const liveNow = ["EXECUTE", "TEST", "PLAN"].includes(card.rawState);

  const reviewLabels = [
    { key: "security", label: "Security", good: "Looks safe", warn: "Needs attention" },
    { key: "exposure", label: "Guardrails", good: "Within limits", warn: "Needs attention" },
    { key: "cleanup", label: "Cleanup", good: "Clear", warn: "Needs attention" },
  ];

  const kitPlan = editor?.item?.payload?.kit_plan;
  const refineNotes = editor?.item?.payload?.jeff?.refine_notes || [];

  return (
    <DetailOverlay variant={variant === "desktop" ? "panel" : "sheet"} onClose={onClose} label={`${card.title} details`} testId="project-detail">
      <ProjectHeading card={card} as="h2" className="pr-12 pt-2" />
      <ProjectNext card={card} className="mt-4" />
      <div className="mt-6 border-t border-os-border">
        <section data-testid="project-detail-section-1" className="border-b border-os-border py-4">
          <Label as="h3">What this project is</Label>
          <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">{card.goal}</p>
        </section>
        {card.why && (
          <section data-testid="project-detail-section-2" className="border-b border-os-border py-4">
            <Label as="h3">Why it matters</Label>
            <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">{card.why}</p>
          </section>
        )}
        {phases.length > 0 && phases[0] !== "The steps aren't written down yet" && (
          <section data-testid="project-detail-section-3" className="border-b border-os-border py-4">
            <Label as="h3">Phases</Label>
            <ol className="mt-1.5 flex flex-col gap-1.5">
              {phases.map((phase, i) => (
                <li key={i} data-testid={`project-detail-phase-${i + 1}`} className="text-[0.875rem] leading-[1.5] text-os-ink-2">
                  {i + 1}. {phase}
                </li>
              ))}
            </ol>
            <MonoMeta as="p" className="mt-2">{`${card.phaseCount} planned steps`}</MonoMeta>
          </section>
        )}
        {liveNow && <RunnerLive slug={card.slug} />}
        {card.definition_of_done && (
          <section data-testid="project-detail-section-3b" className="border-b border-os-border py-4">
            <Label as="h3">Definition of done — how we know we've reached the end</Label>
            <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">{card.definition_of_done}</p>
          </section>
        )}
        {(card.skills?.length || card.resources?.length) ? (
          <section data-testid="project-detail-section-3c" className="border-b border-os-border py-4">
            {card.skills?.length > 0 && (
              <>
                <Label as="h3">Skills prepared for this</Label>
                <ul className="mt-1.5 flex flex-wrap gap-1.5" data-testid="project-detail-skills">
                  {card.skills.map((x) => (
                    <li key={x} className="rounded-full border border-os-border px-2.5 py-0.5 font-mono text-[0.6875rem] text-os-ink-2">{x}</li>
                  ))}
                </ul>
              </>
            )}
            {card.resources?.length > 0 && (
              <>
                <Label as="h3" className="mt-3">Resources in play</Label>
                <ul className="mt-1.5 flex flex-wrap gap-1.5">
                  {card.resources.map((x) => (
                    <li key={x} className="rounded-full border border-os-border px-2.5 py-0.5 font-mono text-[0.6875rem] text-os-ink-2">{x}</li>
                  ))}
                </ul>
              </>
            )}
          </section>
        ) : null}
        <section data-testid="project-detail-section-4" className="border-b border-os-border py-4">
          <Label as="h3">Where we left off</Label>
          <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">{card.lastKitAction}</p>
          <p className="mt-1 text-[0.8125rem] text-os-ink-3">Next: {card.resume?.next || card.next}</p>
          {card.archivedWithReason && (
            <p className="mt-1 text-[0.8125rem] text-os-ink-3">Parked because: {card.archivedWithReason}</p>
          )}
        </section>
        <section data-testid="project-detail-section-5" className="border-b border-os-border py-4">
          <Label as="h3">Review dots</Label>
          <div className="mt-2 flex flex-col gap-2">
            {reviewLabels.map(({ key, label, good, warn }) => {
              const val = card.reviewDots?.[key] || "unset";
              return (
                <div key={key} className="flex items-center gap-2">
                  <span className={`inline-block h-2 w-2 rounded-full ${val === "clear" ? "bg-os-green" : val === "attention" ? "bg-os-amber" : "bg-os-ink-4"}`} />
                  <span className="text-[0.8125rem] font-medium text-os-ink">{label}</span>
                  <span className="text-[0.8125rem] text-os-ink-3">{val === "clear" ? good : val === "attention" ? warn : "Not set yet"}</span>
                </div>
              );
            })}
          </div>
        </section>
        {total > 0 && (
          <section data-testid="project-detail-section-6" className="border-b border-os-border py-4">
            <Label as="h3">Success checks verified</Label>
            <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">
              {checked} of {total} checks verified so far
            </p>
          </section>
        )}
        <section data-testid="project-detail-section-7" className="py-4">
          <Label as="h3">How we work together</Label>
          <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">
            You can leave a note on any card — Kit checks the answers, you verify and approve.
          </p>
        </section>
      </div>
      {editor && (
        <>
          {kitPlan && (
            <section data-testid="project-detail-kit-plan" className="mt-4 border-t border-os-border pt-4">
              <Label as="h3">Kit plan</Label>
              <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">{kitPlan.plan}</p>
              {kitPlan.steps?.length > 0 && (
                <ol className="mt-2 flex flex-col gap-1">
                  {kitPlan.steps.map((s, i) => (
                    <li key={i} className="text-[0.875rem] leading-[1.5] text-os-ink-2">
                      {i + 1}. {s}
                    </li>
                  ))}
                </ol>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  data-testid="kit-plan-approve"
                  disabled={Boolean(editor.board?.pending) || kitPlan.state !== "PLAN-READY"}
                  onClick={async () => {
                    const res = await editor.board.act("approve", editor.item.slug);
                    if (res) {
                      /* close overlay or show flash; let parent page handle */
                    }
                  }}
                  className="rounded-control border border-os-border px-3 py-1.5 font-mono text-[0.6875rem] text-os-ink hover:border-os-border-strong disabled:opacity-50"
                >
                  Approve
                </button>
                <button
                  type="button"
                  data-testid="kit-plan-discard"
                  disabled={Boolean(editor.board?.pending)}
                  onClick={async () => {
                    await editor.board.act("discardKitPlan", editor.item.slug);
                  }}
                  className="rounded-control px-3 py-1.5 font-mono text-[0.6875rem] text-os-ink-3 hover:text-os-ink hover:bg-os-wash disabled:opacity-50"
                >
                  Discard
                </button>
              </div>
            </section>
          )}
          {refineNotes.length > 0 && (
            <section data-testid="project-detail-refine-notes" className="mt-4 border-t border-os-border pt-4">
              <Label as="h3">Goal notes</Label>
              <ul className="mt-2 flex flex-col gap-2">
                {[...refineNotes].reverse().map((n, i) => (
                  <li key={i} className="flex flex-col gap-0.5">
                    <span className="text-[0.9375rem] leading-[1.55] text-os-ink">{n.text}</span>
                    <MonoMeta>{n.at?.slice(0, 10)}</MonoMeta>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <ItemEditorBar
            board={editor.board}
            item={editor.item}
            onClose={onClose}
            testId={`item-editor-project-${card.slug}`}
          />
          {editor.board?.live && (
            <div className="mt-3">
              <RefineNoteOnCard board={editor.board} slug={editor.item.slug} testId={`project-detail-refine-${card.slug}`} />
            </div>
          )}
                  {card.runner?.phases?.length > 0 && (
            <section data-testid="project-detail-runner" className="mt-4 border-t border-os-border pt-4">
              <Label as="h3">Phase runner</Label>
              <p className="mt-1 text-[0.8125rem] leading-[1.5] text-os-ink-3">
                Kit runs the phases and posts a short written update on each — no waiting on you except at confirmed checkpoints.
              </p>
              <ol className="mt-2 flex flex-col gap-2">
                {card.runner.phases.map((ph, i) => (
                  <li key={i} data-testid={`runner-phase-${i + 1}`} className="rounded-md border border-os-border px-3 py-2">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-[0.875rem] font-semibold text-os-ink">{i + 1}. {ph.title}</p>
                      <StateWord tone={ph.status === "done" ? "green" : ph.status === "running" ? "amber" : "muted"}>
                        {ph.status === "done" ? "Done" : ph.status === "running" ? "Running" : "Waiting to run"}
                      </StateWord>
                    </div>
                    {ph.instructions && (
                      <p className="mt-1 whitespace-pre-line text-[0.8125rem] leading-[1.5] text-os-ink-2">{ph.instructions}</p>
                    )}
                    {ph.update && (
                      <p className="mt-1.5 border-l-2 border-os-border pl-2 text-[0.8125rem] leading-[1.5] text-os-ink-2" data-testid={`runner-update-${i + 1}`}>
                        {ph.update}
                      </p>
                    )}
                    {ph.gate && !ph.gate_approved && (
                      <div className="mt-1.5 flex items-center gap-2">
                        <span className="font-mono text-[0.6875rem] text-os-amber">checkpoint — your go-ahead before Kit moves on</span>
                        <button
                          type="button"
                          data-testid={`runner-gate-approve-${i + 1}`}
                          onClick={async () => { await editor.board.edit("phaseApproveGate", editor.item.slug, { move: i, note: "Go ahead." }); }}
                          className="rounded-control border border-os-border px-2.5 py-1 font-mono text-[0.6875rem] text-os-ink hover:border-os-border-strong"
                        >
                          Approve & continue
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          )}
          <section data-testid="project-detail-lifecycle" className="mt-4 border-t border-os-border pt-4">
            <Label as="h3">Project lifecycle</Label>
            <p className="mt-1.5 text-[0.8125rem] leading-[1.5] text-os-ink-3">
              Step back without losing anything — postponed projects wait in "Set aside" on the board; draft mode sends it back to the workshop for re-shaping.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {editor.item.state === "TABLED" ? (
                <button
                  type="button"
                  data-testid="project-resume"
                  onClick={async () => { await editor.board.act("resume", editor.item.slug, "Back on — picking up where it left off."); }}
                  className="rounded-control border border-os-border px-3 py-1.5 font-mono text-[0.6875rem] text-os-ink hover:border-os-border-strong"
                >
                  Resume project
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    data-testid="project-postpone"
                    onClick={async () => { await editor.board.act("postpone", editor.item.slug, "Jeff set this aside."); }}
                    className="rounded-control border border-os-border px-3 py-1.5 font-mono text-[0.6875rem] text-os-ink hover:border-os-border-strong"
                  >
                    Postpone — set aside
                  </button>
                  <button
                    type="button"
                    data-testid="project-backtodraft"
                    onClick={async () => { await editor.board.act("backToDraft", editor.item.slug, "Back to the workshop for re-shaping."); }}
                    className="rounded-control border border-os-border px-3 py-1.5 font-mono text-[0.6875rem] text-os-ink hover:border-os-border-strong"
                  >
                    Return to draft
                  </button>
                </>
              )}
            </div>
          </section>
</>
      )}
    </DetailOverlay>
  );
};
